import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import Topbar from "@/components/Topbar";
import { Plus, PencilSimple, Trash, UploadSimple, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { VEHICLE_BRANDS, VEHICLE_MODELS_BY_BRAND, VEHICLE_COLORS, buildVehicleYears } from "@/data/vehicleData";

const empty = { customer_id: "", label: "", identifier: "", brand: "", model: "", year: "", color: "", vin: "", km: "", last_oil_change_at: "", last_oil_change_km: "", oil_change_interval_km: "10000", last_service_at: "", image_url: "" };
const OIL_INTERVALS = ["5000", "7500", "10000", "15000"];

export default function Vehicles() {
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const yearsList = useMemo(() => buildVehicleYears(), []);
  const modelsForBrand = useMemo(
    () => VEHICLE_MODELS_BY_BRAND[form.brand] || [],
    [form.brand]
  );

  const load = async () => {
    const [v, c] = await Promise.all([api.get("/vehicles"), api.get("/customers")]);
    setRows(v.data);
    setCustomers(c.data);
  };

  useEffect(() => { load(); }, []);

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) {
      toast.error("Imagem deve ter no máximo 4MB.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/vehicles/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((s) => ({ ...s, image_url: data.url }));
      toast.success("Imagem carregada.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha no upload");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async (e) => {
    e.preventDefault();
    const autoLabel = [form.brand, form.model, form.year].filter(Boolean).join(" ").trim();
    const payload = {
      ...form,
      label: autoLabel || form.identifier || "Veículo",
      year: form.year ? parseInt(form.year) : null,
      km: form.km ? parseInt(form.km) : null,
      last_oil_change_km: form.last_oil_change_km ? parseInt(form.last_oil_change_km) : null,
      oil_change_interval_km: form.oil_change_interval_km ? parseInt(form.oil_change_interval_km) : null,
      color: form.color || null,
      vin: form.vin || null,
      last_oil_change_at: form.last_oil_change_at || null,
      last_service_at: form.last_service_at || null,
      image_url: form.image_url || null,
    };
    try {
      if (editing) {
        await api.put(`/vehicles/${editing.id}`, payload);
        toast.success("Veículo atualizado");
      } else {
        await api.post("/vehicles", payload);
        toast.success("Veículo criado");
      }
      setOpen(false); setEditing(null); setForm(empty);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha");
    }
  };

  const remove = async (r) => {
    if (!confirm(`Excluir ${r.label}?`)) return;
    await api.delete(`/vehicles/${r.id}`);
    toast.success("Excluído");
    load();
  };

  const customerName = (id) => customers.find((c) => c.id === id)?.name || "—";

  return (
    <>
      <Topbar
        title="Veículos"
        subtitle="Ativos monitorados pelos agentes."
        actions={
          <button onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} data-testid="vehicles-new-btn" className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-slate-900 text-white hover:bg-slate-800 transition-colors text-[12px] font-medium shadow-sm">
            <Plus size={13} weight="bold" /> Novo veículo
          </button>
        }
      />
      <div className="p-6 md:p-8">
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr>
                <th className="px-4 py-3 text-xs font-medium text-slate-600 uppercase tracking-wider">Placa</th>
                <th className="px-4 py-3 text-xs font-medium text-slate-600 uppercase tracking-wider">Veículo</th>
                <th className="px-4 py-3 text-xs font-medium text-slate-600 uppercase tracking-wider">Cor</th>
                <th className="px-4 py-3 text-xs font-medium text-slate-600 uppercase tracking-wider">Cliente</th>
                <th className="px-4 py-3 text-xs font-medium text-slate-600 uppercase tracking-wider">KM</th>
                <th className="px-4 py-3 text-xs font-medium text-slate-600 uppercase tracking-wider">Última troca de óleo</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan="7" className="p-8 text-center">
                  <div className="text-slate-700 font-medium text-sm mb-1">Nenhum veículo cadastrado ainda</div>
                  <div className="text-xs text-slate-500 leading-relaxed max-w-md mx-auto">
                    Você pode cadastrar manualmente clicando em <span className="font-medium text-slate-700">"Novo veículo"</span> — ou deixar seus clientes mandarem uma foto do CRLV pelo WhatsApp que o AgentUS cadastra automaticamente.
                  </div>
                </td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`vehicle-row-${r.id}`}>
                  <td className="px-4 py-3 font-mono text-xs font-semibold text-slate-900">{r.identifier}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{r.label}</div>
                    <div className="text-xs text-slate-500">{r.brand} {r.model} {r.year || ""}{r.vin ? ` · ${r.vin}` : ""}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{r.color || "—"}</td>
                  <td className="px-4 py-3 text-slate-700">{customerName(r.customer_id)}</td>
                  <td className="px-4 py-3 text-slate-700 text-right">{r.km ? r.km.toLocaleString("pt-BR") : "—"}</td>
                  <td className="px-4 py-3 text-slate-700 text-xs">{r.last_oil_change_at ? new Date(r.last_oil_change_at).toLocaleDateString("pt-BR") : "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => { setEditing(r); setForm({
                      customer_id: r.customer_id, label: r.label, identifier: r.identifier,
                      brand: r.brand || "", model: r.model || "", year: r.year || "",
                      color: r.color || "", vin: r.vin || "", km: r.km || "",
                      last_oil_change_at: r.last_oil_change_at ? r.last_oil_change_at.slice(0, 10) : "",
                      last_oil_change_km: r.last_oil_change_km || "",
                      oil_change_interval_km: r.oil_change_interval_km || "10000",
                      last_service_at: r.last_service_at ? r.last_service_at.slice(0, 10) : "",
                      image_url: r.image_url || "",
                    }); setOpen(true); }} className="p-1.5 rounded hover:bg-slate-100 text-slate-600"><PencilSimple size={16} /></button>
                    <button onClick={() => remove(r)} className="p-1.5 rounded hover:bg-rose-50 text-rose-600"><Trash size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-white rounded-md border border-slate-200 w-full max-w-lg p-6 space-y-3 max-h-[90vh] overflow-y-auto" data-testid="vehicle-modal">
            <h3 className="font-heading text-xl font-semibold">{editing ? "Editar" : "Novo"} veículo</h3>
            <div className="grid grid-cols-2 gap-3">
              <label className="block col-span-2">
                <span className="text-xs font-medium text-slate-600 uppercase tracking-wider">Cliente</span>
                <select required value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white" data-testid="vehicle-form-customer">
                  <option value="">—</option>
                  {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-xs font-medium text-slate-600 uppercase tracking-wider">Placa</span>
                <input required value={form.identifier} onChange={(e) => setForm({ ...form, identifier: e.target.value.toUpperCase() })} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono" data-testid="vehicle-form-plate" />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-slate-600 uppercase tracking-wider">Marca</span>
                <input
                  list="vehicle-brands"
                  value={form.brand}
                  onChange={(e) => setForm({ ...form, brand: e.target.value, model: "" })}
                  placeholder="Digite ou selecione…"
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                  data-testid="vehicle-form-brand"
                />
                <datalist id="vehicle-brands">
                  {VEHICLE_BRANDS.map((b) => <option key={b} value={b} />)}
                </datalist>
              </label>
              <label className="block">
                <span className="text-xs font-medium text-slate-600 uppercase tracking-wider">Modelo</span>
                <input
                  list="vehicle-models"
                  value={form.model}
                  onChange={(e) => setForm({ ...form, model: e.target.value })}
                  placeholder={form.brand ? `Modelos ${form.brand}…` : "Escolha a marca primeiro"}
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                  data-testid="vehicle-form-model"
                />
                <datalist id="vehicle-models">
                  {modelsForBrand.map((m) => <option key={m} value={m} />)}
                </datalist>
              </label>
              <label className="block">
                <span className="text-xs font-medium text-slate-600 uppercase tracking-wider">Ano</span>
                <input
                  list="vehicle-years"
                  inputMode="numeric"
                  value={form.year}
                  onChange={(e) => setForm({ ...form, year: e.target.value.replace(/\D/g, "").slice(0, 4) })}
                  placeholder="Selecione ou digite…"
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                  data-testid="vehicle-form-year"
                />
                <datalist id="vehicle-years">
                  {yearsList.map((y) => <option key={y} value={y} />)}
                </datalist>
              </label>
              <label className="block">
                <span className="text-xs font-medium text-slate-600 uppercase tracking-wider">Cor</span>
                <input
                  list="vehicle-colors"
                  value={form.color}
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                  placeholder="Selecione ou digite…"
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                  data-testid="vehicle-form-color"
                />
                <datalist id="vehicle-colors">
                  {VEHICLE_COLORS.map((c) => <option key={c} value={c} />)}
                </datalist>
              </label>
              <label className="block col-span-2"><span className="text-xs uppercase tracking-wider text-slate-600">Chassi (VIN)</span><input value={form.vin} onChange={(e) => setForm({ ...form, vin: e.target.value.toUpperCase() })} placeholder="17 caracteres" maxLength={17} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono uppercase" data-testid="vehicle-form-vin" /></label>
              <label className="block"><span className="text-xs uppercase tracking-wider text-slate-600">KM atual</span><input type="number" value={form.km} onChange={(e) => setForm({ ...form, km: e.target.value })} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" data-testid="vehicle-form-km" /></label>
              <label className="block">
                <span className="text-xs uppercase tracking-wider text-slate-600">Intervalo da troca de óleo (km)</span>
                <input
                  list="oil-intervals"
                  type="number"
                  value={form.oil_change_interval_km}
                  onChange={(e) => setForm({ ...form, oil_change_interval_km: e.target.value })}
                  placeholder="10000"
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                  data-testid="vehicle-form-oil-interval"
                />
                <datalist id="oil-intervals">
                  {OIL_INTERVALS.map((v) => <option key={v} value={v} />)}
                </datalist>
              </label>
              <label className="block"><span className="text-xs uppercase tracking-wider text-slate-600">Data da última troca</span><input type="date" value={form.last_oil_change_at} onChange={(e) => setForm({ ...form, last_oil_change_at: e.target.value })} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" data-testid="vehicle-form-last-oil-at" /></label>
              <label className="block"><span className="text-xs uppercase tracking-wider text-slate-600">KM da última troca</span><input type="number" value={form.last_oil_change_km} onChange={(e) => setForm({ ...form, last_oil_change_km: e.target.value })} placeholder="ex: 38000" className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" data-testid="vehicle-form-last-oil-km" /></label>
              <label className="block col-span-2"><span className="text-xs uppercase tracking-wider text-slate-600">Última revisão</span><input type="date" value={form.last_service_at} onChange={(e) => setForm({ ...form, last_service_at: e.target.value })} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" /></label>

              <div className="col-span-2">
                <span className="text-xs uppercase tracking-wider text-slate-600">Foto do veículo (opcional)</span>
                <div className="mt-1 flex items-start gap-3">
                  <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="vehicle-form-image-upload" className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-60">
                    <UploadSimple size={16} /> {uploading ? "Enviando…" : form.image_url ? "Trocar foto" : "Selecionar do dispositivo"}
                  </button>
                  <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={onPickFile} className="hidden" data-testid="vehicle-form-image-input" />
                  {form.image_url && (
                    <div className="relative">
                      <img src={form.image_url} alt="prévia" className="w-20 h-20 object-cover rounded-md border border-slate-200" />
                      <button type="button" onClick={() => setForm({ ...form, image_url: "" })} className="absolute -top-2 -right-2 bg-white border border-slate-300 rounded-full p-0.5 shadow-sm hover:bg-rose-50 text-rose-600" data-testid="vehicle-form-image-clear">
                        <X size={12} />
                      </button>
                    </div>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-1">PNG/JPG/WEBP, até 4MB.</p>
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setOpen(false)} className="text-sm text-slate-600">Cancelar</button>
              <button type="submit" className="rounded-md bg-blue-700 text-white px-4 py-2 text-sm font-medium hover:bg-blue-800" data-testid="vehicle-form-submit">Salvar</button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
